import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface OutageNotification {
  id: string;
  title: string;
  description: string | null;
  outage_type: string;
  severity: string;
  status: string;
  scheduled_start: string | null;
  scheduled_end: string | null;
  actual_start: string | null;
  actual_end: string | null;
  affected_services: string[];
  is_published: boolean;
  notifications_sent_at: string | null;
  resolution_sent_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  resolution_notes: string | null;
}

/**
 * Hook to fetch published outage notifications for regular users
 */
export const useOutageNotifications = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["outage-notifications", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("outage_notifications")
        .select("*")
        .eq("is_published", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching outage notifications:", error);
        throw error;
      }

      return data as OutageNotification[];
    },
    enabled: !!user?.id,
  });
};

/**
 * Hook to fetch all outage notifications (for superuser management)
 */
export const useAllOutageNotifications = () => {
  return useQuery({
    queryKey: ["outage-notifications-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("outage_notifications")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching all outage notifications:", error);
        throw error;
      }

      return data as OutageNotification[];
    },
  });
};

/**
 * Hook to create a new outage notification
 */
export const useCreateOutageNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<OutageNotification>) => {
      // Ensure required fields are present
      if (!data.title || !data.outage_type || !data.severity || !data.status) {
        throw new Error("Missing required fields");
      }
      
      const { error } = await supabase
        .from("outage_notifications")
        .insert([data as any]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["outage-notifications-all"] });
      queryClient.invalidateQueries({ queryKey: ["outage-notifications"] });
    },
  });
};

/**
 * Hook to update an outage notification
 */
export const useUpdateOutageNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<OutageNotification> }) => {
      const { error } = await supabase
        .from("outage_notifications")
        .update(data)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["outage-notifications-all"] });
      queryClient.invalidateQueries({ queryKey: ["outage-notifications"] });
    },
  });
};

/**
 * Hook to delete an outage notification
 */
export const useDeleteOutageNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("outage_notifications")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["outage-notifications-all"] });
      queryClient.invalidateQueries({ queryKey: ["outage-notifications"] });
    },
  });
};

/**
 * Hook to get count of unseen active outages
 */
export const useUnseenOutageCount = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ["outage-notifications-unseen-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      
      // Get published active outages
      const { data: activeOutages, error: outagesError } = await supabase
        .from("outage_notifications")
        .select("id")
        .eq("is_published", true)
        .in("status", ["scheduled", "in_progress"]);
      
      if (outagesError) throw outagesError;
      if (!activeOutages || activeOutages.length === 0) return 0;
      
      // Get viewed outages
      const { data: viewedOutages, error: viewedError } = await supabase
        .from("outage_notification_views")
        .select("outage_id")
        .eq("user_id", user.id);
      
      if (viewedError) throw viewedError;
      
      const viewedIds = new Set(viewedOutages?.map(v => v.outage_id) || []);
      const unseenCount = activeOutages.filter(o => !viewedIds.has(o.id)).length;
      
      return unseenCount;
    },
    enabled: !!user?.id,
  });
};

/**
 * Hook to mark a single outage as viewed
 */
export const useMarkOutageAsViewed = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (outageId: string) => {
      if (!user?.id) return;
      
      const { error } = await supabase
        .from("outage_notification_views")
        .insert([{ user_id: user.id, outage_id: outageId }]);

      // Ignore unique constraint violations (already viewed)
      if (error && !error.message.includes("duplicate")) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["outage-notifications-unseen-count"] });
    },
  });
};

/**
 * Hook to mark all active outages as viewed
 */
export const useMarkAllOutagesAsViewed = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) return;
      
      // Get all active outages
      const { data: activeOutages, error: fetchError } = await supabase
        .from("outage_notifications")
        .select("id")
        .eq("is_published", true)
        .in("status", ["scheduled", "in_progress"]);
      
      if (fetchError) throw fetchError;
      if (!activeOutages || activeOutages.length === 0) return;
      
      // Insert views for all active outages
      const views = activeOutages.map(outage => ({
        user_id: user.id,
        outage_id: outage.id
      }));
      
      const { error } = await supabase
        .from("outage_notification_views")
        .upsert(views, { onConflict: 'user_id,outage_id', ignoreDuplicates: true });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["outage-notifications-unseen-count"] });
    },
  });
};
